import { Modal, Button, Table } from "react-bootstrap";
import { useState, useEffect, useCallback } from "react";
import currency from "currency.js";
import { confirmable, createConfirmation } from "react-confirm";
import Select from "react-select";
import * as yup from "yup";

import "../../assets/scss/scoped/issueItem.scss";
import { fetchActionsUtil, formatForQty } from "../../utils/helpers";
import ModalLoader from "../utils/ModalLoader";
import NewRequisitionModal from "../Requisition/NewRequisition";
import { useBackendUrl } from "../../utils/hooks";
import ConfirmDialog from "../ConfirmDialogue";
import { backendApis } from "../../config";
import { useFormik } from "formik";
import { CustomInput } from "../utils/CustomInput";

function ShowProjectedItemsModal({ proceed, show, mainData }) {
  const [activeTableIndex, setActiveTableIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [itemsRequested, setItemsRequested] = useState([]);
  const [showNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  //=====================
  const [allstaff, setAllStaff] = useState([]);
  const [allWarehouse, setAllWarehouse] = useState([]);
  // ====================

  const backendUrl = useBackendUrl();

  const handleInputChange = ({ index, name, value }) => {
    const { prod_profiles } = selectedItem;
    const oldTableData = prod_profiles;
    oldTableData[index][name] = value;
    setSelectedItem((item) => ({ ...item, prod_profiles: [...oldTableData] }));
  };

  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);

  const plantLocal = JSON.parse(window.localStorage.getItem("plantDetails"));

  const issuedToData = backendApis.map((el) => ({
    value: el.name,
    label: el.name,
  }));

  const fetchBatches = useCallback(
    async (items) => {
      setIsLoadingAddToCart(true);
      try {
        const data = await fetchActionsUtil(
          `${backendUrl}/api/production/get-production-batch`,
          "POST",
          "",
          { items, Plant: plantLocal.PLID }
        );
        return data?.batches || [];
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingAddToCart(false);
      }
    },
    [backendUrl, plantLocal.PLID]
  );

  const formik = useFormik({
    initialValues: {
      Warehouse: "",
      WarehouseName: "",
      issueTo: "",
      Requester: "",
      receivingofficer: "",
      RequesterName: "",
      Quantity: "",
    },
    validationSchema: yup.object().shape({
      Warehouse: yup.string().required("Warehouse is required"),
      issueTo: yup.string().required("Issue To is required"),
      Quantity: yup.string().required("Quantity is required"),
      receivingofficer: yup.string().required("Receiving Officer is required"),
    }),
    onSubmit: async (values) => {
      const { prod_profiles = [], ...rest } = selectedItem;

      const products = { ...rest, ...values };

      const quantity = parseInt(products?.Quantity);
      const errors = [];

      // Validate quantities
      for (const { quantity: q, actual, title } of prod_profiles) {
        const estimated = parseInt(q) * quantity;
        const actualVal = parseInt(actual);

        if (estimated > actualVal) {
          errors.push({
            type: `${title} Quantity`,
            message: "The estimated quantity is more than the actual quantity",
          });
        } else if (actualVal > estimated) {
          errors.push({
            type: `${title} Quantity`,
            message: "The actual quantity is more than the estimated quantity",
          });
        }
      }

      // Confirm dialog if there are validation errors
      if (errors.length > 0) {
        const confirmed = await ConfirmDialog({
          title: `${errors.length} Error(s) Found`,
          description: `${errors
            ?.map((error) => `${error.type}: ${error.message}`)
            .join("\n")}\nAre you sure you want to continue?`,
        });

        if (!confirmed) return;
      }

      // Calculate total amount and proceed
      const totalAmount = prod_profiles.reduce(
        (sum, { actual, UnitCost }) =>
          sum + parseInt(actual) * parseFloat(UnitCost),
        0
      );

      const unit = Number((parseFloat(totalAmount) / quantity).toFixed(2));

      const UnitCost = unit;
      const UnitPrice = unit;

      proceed({ ...products, UnitCost, UnitPrice, totalAmount, prod_profiles });
    },
  });

  const getStaffs = async (name) => {
    const sendTo = `${
      backendApis.find((el) => el.name === name)?.url
    }/api/users`;
    const staffs = await fetchActionsUtil(sendTo, "GET");
    staffs.staff = staffs.staff.map((el) => ({
      value: `${el.Name}-${el.email}`,
      label: el.Name,
    }));
    setAllStaff(staffs.staff);
  };

  const getWarehouse = async (name) => {
    const sendTo = `${
      backendApis.find((el) => el.name === name)?.url
    }/api/warehouse`;
    const { warehouses } = await fetchActionsUtil(sendTo, "GET");
    const ware = [
      {
        value: "",
        label: "Select Warehouse",
      },
      {
        value: "Sales",
        label: "Sales",
      },
      ...warehouses?.map((el) => ({
        value: `${el.W_ID}`,
        label: el.W_name,
      })),
    ];
    setAllWarehouse(ware);
  };

  useEffect(() => {
    const organize = async () => {
      const { prod_profiles, ...rest } = mainData;

      formik.setFieldValue("Warehouse", rest?.Warehouse || "");
      formik.setFieldValue("WarehouseName", rest?.WarehouseName || "");
      formik.setFieldValue("issueTo", rest?.issueTo || "");
      formik.setFieldValue("Requester", rest?.Requester || "");
      formik.setFieldValue("receivingofficer", rest?.receivingofficer || "");
      formik.setFieldValue("RequesterName", rest?.RequesterName || "");
      formik.setFieldValue("Quantity", rest?.Quantity || 1);

      getStaffs(rest?.issueTo);
      getWarehouse(rest?.issueTo);
      const data = await fetchBatches(prod_profiles);

      const newProfiles = data?.map((el) => ({
        ...el,
        actual: el?.actual || el?.quantity * mainData.Quantity,
        damage: el?.damage || 0,
      }));

      setSelectedItem({ prod_profiles: newProfiles, ...rest });
    };

    organize();
  }, [mainData, fetchBatches]);

  const addToTable = async (e) => {
    if (e.key === "Enter") {
      const { prod_profiles, ...rest } = selectedItem;

      const newPro = prod_profiles?.map((el) => ({
        ...el,
        quantity: parseFloat(el?.actual) + parseFloat(el?.damage),
        remark: el?.remark || "",
      }));

      const newitems = { ...rest, prod_profiles: newPro };

      try {
        const { message } = await fetchActionsUtil(
          `${backendUrl}/api/production/check-quantity-items/${
            plantLocal.PLID
          }`,
          "POST",
          "",
          newitems
        );

        setIsLoadingAddToCart(false);
        if (message.length > 0) {
          setItemsRequested(message?.map((er) => er.item));
          return setErrorMessage("Item Required not enough");
        } else {
          setErrorMessage(null);
          setItemsRequested([]);
        }
      } catch (error) {
        console.log(error);
        return setIsLoadingAddToCart(false);
      }

      setSelectedItem((item) => ({ ...item, prod_profiles }));
    }
  };

  const multiplyQuantity = (quantity) => {
    const { prod_profiles } = selectedItem;

    const newTableData = prod_profiles?.map((el) => ({
      ...el,
      actual: parseInt(el.quantity) * parseInt(quantity),
    }));

    setSelectedItem((item) => ({ ...item, prod_profiles: newTableData }));
  };

  return (
    <Modal
      onHide={() => proceed(false)}
      show={show}
      backdropClassName={`global-backdrop`}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="my-style-modal"
      animation={false}
      enforceFocus={false}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h1> Raw Materials</h1>
          </div>

          <p>Edit each raw material quantity by changing the input.</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className="container">
            <div>
              <div>
                {/* <div>show here</div> */}

                <div className="col-4 mb-3 d-flex align-items-end gap-2">
                  <div className="flex-grow-1">
                    <CustomInput
                      name="Quantity"
                      placeholder="Enter Quantity"
                      typeOfInput="text"
                      label="Quantity"
                      value={formik.values?.Quantity}
                      onChange={(e) => {
                        formik.handleChange(e);
                        multiplyQuantity(e.target.value);
                      }}
                    />
                    {formik.touched?.Quantity && !!formik.errors?.Quantity ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Quantity}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/*  */}
                {/* Table */}

                <div className="mb-4">
                  <Table
                    responsive
                    borderless
                    striped
                    className="product-table"
                  >
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Bar Code</th>
                        <th>Name</th>
                        <th>Estimated Qty</th>
                        <th>Actual Qty</th>
                        <th>Damages</th>
                        <th>UOM</th>
                        <th>Unit Cost</th>
                        <th>Sub Total</th>
                        <th>Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem?.prod_profiles?.length > 0 &&
                        selectedItem?.prod_profiles?.map((el, index) => (
                          <tr
                            key={index}
                            className={`${activeTableIndex === index &&
                              "active-row"}`}
                            onClick={() =>
                              activeTableIndex !== index &&
                              setActiveTableIndex(index)
                            }
                          >
                            <td>{index + 1}</td>
                            <td>{el.item}</td>
                            <td>{el.title}</td>
                            <td className="text-center">
                              {currency(el.quantity, {
                                symbol: "",
                                format: formatForQty,
                              })
                                .multiply(formik.values?.Quantity)
                                .format()}
                            </td>
                            <td>
                              <input
                                className="border-0 text-center w-100 form-control"
                                type="number"
                                value={el?.actual}
                                onKeyDown={(e) => addToTable(e)}
                                name="actual"
                                onChange={(e) =>
                                  handleInputChange({
                                    index,
                                    name: e.target.name,
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="border-0 text-center w-100 form-control"
                                type="number"
                                value={el.damage}
                                onKeyDown={(e) => addToTable(e)}
                                name="damage"
                                onChange={(e) =>
                                  handleInputChange({
                                    index,
                                    name: e.target.name,
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>{el?.Product_Model || ""}</td>
                            <td>
                              {currency(parseFloat(el?.UnitCost), {
                                symbol: "",
                              }).format()}
                            </td>
                            <td>
                              {currency(
                                parseFloat(el?.UnitCost) *
                                  parseFloat(el?.actual),
                                {
                                  symbol: "",
                                }
                              ).format()}
                            </td>
                            <td>
                              <div>
                                <textarea
                                  className="form-control"
                                  name="remark"
                                  cols={20}
                                  rows={2}
                                  onChange={(e) =>
                                    handleInputChange({
                                      index,
                                      name: e.target.name,
                                      value: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>

                {/*  */}
                <div className="m-4 row">
                  <div className="col-4 mb-3">
                    <label className="form-label mb-0">Issue to </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={issuedToData.find(
                        (el) => el.value === formik.values?.issueTo
                      )}
                      onChange={({ value }) => {
                        formik.setFieldValue("issueTo", value);
                        getStaffs(value);
                        getWarehouse(value);
                      }}
                      options={issuedToData}
                    />

                    {formik.touched?.issueTo && !!formik.errors?.issueTo ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.issueTo}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-4 mb-3">
                    <label className="form-label mb-0">Warehouse </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={allWarehouse.find(
                        (el) => el.value === formik.values?.Warehouse
                      )}
                      onChange={({ value, label }) => {
                        formik.setFieldValue("Warehouse", value);
                        formik.setFieldValue("WarehouseName", label);
                        formik.setFieldValue("Requester", value);
                      }}
                      options={allWarehouse}
                    />

                    {formik.touched?.Warehouse && !!formik.errors?.Warehouse ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Warehouse}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-4 mb-3">
                    <label className="form-label">Receiving Officer </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={allstaff.find(
                        (el) => el.value === formik.values?.receivingofficer
                      )}
                      onChange={({ value }) => {
                        formik.setFieldValue("receivingofficer", value);
                        formik.setFieldValue("RequesterName", value);
                      }}
                      options={allstaff}
                    />

                    {formik.touched?.receivingofficer &&
                    !!formik.errors?.receivingofficer ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.receivingofficer}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/*  */}

                {errorMessage && (
                  <div className="d-flex justify-content-center">
                    <p className="text-danger">
                      <b>{errorMessage}</b>
                    </p>
                  </div>
                )}
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    onClick={() => proceed(false)}
                    variant="white"
                    className="border bg-white mx-4"
                  >
                    Cancel
                  </Button>
                  {!errorMessage && (
                    <Button
                      onClick={formik.handleSubmit}
                      variant="primary"
                      autoFocus={true}
                    >
                      Save Actual
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <ModalLoader show={isLoadingAddToCart} />

            {showNewRequisitionModal && (
              <NewRequisitionModal
                modalTitle="Send for Approval"
                defaultFormValues={{
                  title: ``,
                  description: "",
                  type: "Material Request",
                }}
                itemsRequested={itemsRequested}
                setShowNewRequisitionModal={setShowNewRequisitionModal}
              />
            )}

            {/*  */}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default function ShowProjectedItemsDialog(props) {
  return createConfirmation(confirmable(ShowProjectedItemsModal))(props);
}
